import { useState } from 'react';
import { Check, Compass } from 'lucide-react';
import { Button } from '@/components/ui/button';

/** What the steering options can do: send while the run works, or only show once it has ended (or in a preview). */
export interface SteerProps {
  /** The run that posted the report is still going, so a choice reaches its agent now. */
  active: boolean;
  /** Options someone has already sent (a later comment from a person says it). */
  sent: readonly string[];
  /** Posts the @AI builder comment for an option. */
  onSteer?: (option: string) => void;
}

/**
 * T-44: the steering suggestions at the end of the coding agent's progress report (a factory-steer block
 * in the comment). While its run works, each is a button that posts it as an @AI builder comment, which
 * goes into the agent's running turn (T-41); sent ones show a tick. Once the run has ended, or without
 * onSteer, they're listed only.
 */
export function FactorySteerOptions({ options, steer }: { options: string[]; steer?: SteerProps }) {
  const [justSent, setJustSent] = useState<string[]>([]);
  const sent = new Set([...(steer?.sent ?? []), ...justSent]);
  const live = Boolean(steer?.active && steer.onSteer);

  if (!live) {
    return (
      <div className="space-y-1 rounded-md border border-dashed px-2.5 py-1.5 text-xs text-muted-foreground">
        <p className="flex items-center gap-1.5 font-medium">
          <Compass className="size-3.5" aria-hidden />
          {steer && !steer.active ? 'It offered these while it worked' : 'Steer the AI builder'}
        </p>
        <ul className="list-disc space-y-0.5 pl-5">
          {options.map((option) => (
            <li key={option}>
              {option}
              {sent.has(option) ? <span className="ml-1 text-2xs">(sent)</span> : null}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Compass className="size-3.5" aria-hidden />
        Steer it, or write your own @AI builder comment
      </p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const done = sent.has(option);
          return (
            <Button
              key={option}
              variant="outline"
              size="xs"
              className="h-auto min-h-6 max-w-full whitespace-normal py-1 text-left"
              disabled={done}
              data-hint={done ? 'Sent to the AI builder' : 'Sends this to the AI builder while it works'}
              onClick={() => {
                setJustSent((prev) => [...prev, option]);
                steer!.onSteer!(option);
              }}
            >
              {done ? <Check aria-hidden /> : null}
              {option}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
